import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import {
    Alert,
    BasicInput,
    NumberInput,
    palette,
    Paper,
    PrimaryButton,
    SimplePopover,
    Typography as T,
} from '@dev/ee-components';
import { ContactField, ContactFieldType } from '@dev/ee-api/ee-api-models';

import {
    PutContactFieldStart,
    SetCustomField,
    SetCurrentFieldType,
    getCustomFieldsStore,
    PutContactFieldError,
} from '@/store/customFields/actions';
import { isReseller } from '@/services/Secuirty';
import { CustomFieldTypes } from '@/services/dictionaries/CustomFieldTypes';
import { links } from '@/services/dictionaries/links';
import { CUSTOM_FIELD_DEFAULT_FIELD_LENGTH, CUSTOM_FIELD_MAX_FIELD_LENGTH } from '@/services/constants';
import { useFieldTypes } from '../hooks/useFieldTypes';
import { useDataTypes } from '../hooks/useDataTypes';
import {
    AddCustomFieldButtonProps,
    DataTypeInputProps,
    FieldNameInputProps,
    FieldTypeInputProps,
} from '../CustomFields';
import { validateCustomFieldName } from '../tools';

const DescriptionWrapper = styled.div`
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid ${palette.gray[3]};
`;

const InputWrapper = styled.div`
    display: flex;
    margin-top: 24px;
    align-items: baseline;
`;

const InputContainer = styled.div`
    display: inline-block;
    &.addCustomFieldButton {
        align-self: flex-end;
    }
    & > div {
        width: 100%;
    }
    & + & {
        padding-left: 24px;
    }
    &.FieldTypeContainer,
    &.LengthContainer {
        width: 25%;
    }
    &.DataTypeContainer {
        width: 18%;
    }
    &.NameContainer {
        width: 42%;
        &.TextContainer {
            width: 40%;
        }
    }
    .MuiButton-endIcon.MuiButton-iconSizeMedium {
        right: 12px;
    }
    .inputWrapperBasicInput {
        & + span {
            position: absolute;
        }
        & > div.Mui-error {
            margin-bottom: 0px;
        }
        & > div.Mui-Focused {
            background: red;
            padding-block: 12px;
        }
    }
    & .iconWrapperBottomNI {
        height: 20px;
    }
`;

const NewFieldsContainer = styled.div`
    margin-bottom: 32px;
`;

const FieldTypeInput = (props: FieldTypeInputProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const dataTypes = useDataTypes();
    const { setIsCustomField, setFieldError } = props;
    const { currentFieldType } = useSelector(getCustomFieldsStore);

    const setPrefabFields = (prefab: string): void => {
        const newField: ContactField = {
            Name: prefab,
        };

        setIsCustomField(prefab === CustomFieldTypes.Custom);

        switch (prefab) {
            case CustomFieldTypes.Custom:
                newField.Type = ContactFieldType.Text;
                newField.Length = CUSTOM_FIELD_DEFAULT_FIELD_LENGTH;
                newField.Name = t('name');
                break;
            case CustomFieldTypes.Birthday:
                newField.Type = ContactFieldType.Date;
                break;
            case CustomFieldTypes.Consent:
                newField.Type = ContactFieldType.Boolean;
                break;
            default:
                newField.Type = ContactFieldType.Text;
                newField.Length = CUSTOM_FIELD_DEFAULT_FIELD_LENGTH;
                break;
        }
        dispatch(SetCustomField(newField));
    };

    const handleChangeFieldType = (fieldType: string): void => {
        dispatch(SetCurrentFieldType(fieldType));
        setFieldError(null);
        setPrefabFields(fieldType);
    };

    return (
        <InputContainer className="FieldTypeContainer">
            <SimplePopover
                id="field-type"
                label={t('fieldtype')}
                options={dataTypes}
                defaultPlaceholder={t('choosefieldtype')}
                type="input"
                fullWidth
                value={currentFieldType}
                onItemClick={(e, obj): void => {
                    handleChangeFieldType(obj.value as string);
                }}
                maxHeight={400}
            />
        </InputContainer>
    );
};

const DataTypeInput = (props: DataTypeInputProps): JSX.Element => {
    const { t } = useTranslation();
    const fieldTypes = useFieldTypes();
    const { newField } = useSelector(getCustomFieldsStore);
    const dispatch = useDispatch();
    const { isCustomField, setFieldError } = props;

    const handleSetCustomFields = (fieldType: string): void => {
        dispatch(
            SetCustomField({
                Name: newField?.Name,
                Type: fieldType,
                Length: fieldType === 'Text' ? newField?.Length : 0,
            })
        );
        setFieldError(null);
    };

    return (
        <InputContainer className="DataTypeContainer">
            <SimplePopover
                id="data-type"
                label={t('datatype')}
                options={fieldTypes}
                disableSentenceCase
                type="input"
                fullWidth
                disabled={!isCustomField}
                value={newField?.Type}
                onItemClick={(e, obj): void => {
                    handleSetCustomFields(obj.value as string);
                }}
            />
        </InputContainer>
    );
};

const FieldLengthInput = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { newField } = useSelector(getCustomFieldsStore);

    const handleFieldLengthChange = (event): void => {
        const newFieldObj = { ...newField };
        const length = event.value ? event.value : event.currentTarget.value;
        newFieldObj.Length = length < CUSTOM_FIELD_MAX_FIELD_LENGTH ? length : CUSTOM_FIELD_MAX_FIELD_LENGTH;
        dispatch(SetCustomField(newFieldObj));
    };
    return (
        <InputContainer className="LengthContainer">
            <NumberInput
                id="field-length-input"
                name="FieldLength"
                defaultValue={100}
                min={0}
                max={CUSTOM_FIELD_MAX_FIELD_LENGTH}
                step={1}
                label={t('fieldlength')}
                onClickButton={handleFieldLengthChange}
                onChange={handleFieldLengthChange}
            />
        </InputContainer>
    );
};

const FieldNameInput = (props: FieldNameInputProps): JSX.Element => {
    const { t } = useTranslation();
    const { collection, newField, nameFieldBackendError, currentFieldType } = useSelector(getCustomFieldsStore);
    const dispatch = useDispatch();
    const { fieldError, setFieldError } = props;

    useEffect(() => {
        if (!!newField && !nameFieldBackendError) {
            const error = validateCustomFieldName(newField?.Name, collection);
            return setFieldError(error);
        }
        setFieldError(nameFieldBackendError);
    }, [nameFieldBackendError]);

    const setFieldName = (fieldName: string): void => {
        dispatch(PutContactFieldError(currentFieldType, null));
        const error = validateCustomFieldName(fieldName, collection);

        dispatch(
            SetCustomField({
                Name: fieldName,
                Type: newField ? newField.Type : ContactFieldType.Text,
                Length: newField ? newField.Length : 100,
            })
        );

        if (fieldError !== error) {
            setFieldError(error);
        }
    };

    return (
        <InputContainer className={newField?.Type === 'Text' ? 'NameContainer TextContainer' : 'NameContainer'}>
            <BasicInput
                id="field-name-input"
                label={t('fieldname')}
                type="input"
                value={newField?.Name || ''}
                error={!!fieldError}
                errorMessage={!!fieldError && fieldError}
                onChange={(e): void => {
                    setFieldName(e.currentTarget.value);
                }}
            />
        </InputContainer>
    );
};

const AddCustomFieldButton = (props: AddCustomFieldButtonProps): JSX.Element => {
    const { t } = useTranslation();
    const { collection, isAddingField, newField, currentFieldType } = useSelector(getCustomFieldsStore);
    const dispatch = useDispatch();
    const { fieldError } = props;

    const handleAddField = (): void => {
        const newfield = { ...newField };
        const fieldnames = collection?.map((field): string => field.Name) || [];

        fieldnames.push('email', 'firstname', 'lastname');
        if (newField.Type.toLowerCase() !== 'text' && newField.Length !== 0) {
            newfield.Length = 0;
        }

        dispatch(PutContactFieldStart(newfield, currentFieldType));
    };

    return (
        <InputContainer className="addCustomFieldButton">
            <PrimaryButton
                id={generateIdBasedOnPath('add-custom-fields-button')}
                disabled={!!fieldError || !newField?.Name || isAddingField || !currentFieldType}
                text={t('addcustomfields')}
                onClick={handleAddField}
            />
        </InputContainer>
    );
};

export const AddNewField = (): JSX.Element => {
    const { t } = useTranslation();
    const { newField } = useSelector(getCustomFieldsStore);
    const [isCustomField, setIsCustomField] = useState(false);
    const [fieldError, setFieldError] = useState(null);

    return (
        <NewFieldsContainer>
            <Paper padding={{ top: 32, right: 48, bottom: 32, left: 48 }}>
                <DescriptionWrapper>
                    <div style={{ marginBottom: '8px' }}>
                        <T variant="h3">{t('customfields')}</T>
                    </div>
                    <T variant="body1">
                        {t('managecontactfields')} {!isReseller() && t('needtips')}
                    </T>
                    {!isReseller() && (
                        <T variant="body1">
                            <a rel="noreferrer" href={links.creatingCustomFields} target="_blank">
                                {t('findmorecustomfields')}
                            </a>
                        </T>
                    )}
                </DescriptionWrapper>
                <Alert severity="info" body={t('managecontactsimportant')} />
                <InputWrapper>
                    <FieldTypeInput setIsCustomField={setIsCustomField} setFieldError={setFieldError} />
                    <DataTypeInput isCustomField={isCustomField} setFieldError={setFieldError} />

                    {newField?.Type === 'Text' && <FieldLengthInput />}
                    <FieldNameInput fieldError={fieldError} setFieldError={setFieldError} />
                    <AddCustomFieldButton fieldError={fieldError} />
                </InputWrapper>
            </Paper>
        </NewFieldsContainer>
    );
};
